import * as React from 'react';
import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';
import LayoutAuth from '../Layouts/Auth';
import PlannerAssistant from '../components/PlannerAssistant/PlannerAssistant';
import SignIn from '../Pages/SignIn';
import AdminSignIn from '../Pages/AdminSignIn';
import { Private } from '../modules/auth/containers';
import RoutesPage from '../Pages/Routes';
import RouteDetailPage from '../Pages/RouteDetail';
import LogoutPage from '../Pages/Logout';
import HubsPage from '../Pages/Hubs';
import FleetsPage from '../Pages/Fleets';
import NewOrderPage from '../Pages/NewOrder';
import OrdersPage from '../Pages/Orders';
import UploadOrders from '../Pages/UploadOrders';
import AdminCatalogPage from '../Pages/AdminCatalogPage';
import OrderDetailPage from '../Pages/OrderDetail';
import DriversPage from '../Pages/Drivers';
import DriverDetailPage from '../Pages/DriverDetail';
import SchedulerPage from '../Pages/Scheduler';
import RoutesChart from '../Pages/RoutesChart';
import AdminUsersPage from '../Pages/AdminUsers';
import PartnersPage from '../Pages/Partners';
import PartnerDetailPage from '../Pages/PartnerDetail';
import PartnerRouteDetailPage from '../Pages/PartnerRouteDetail';
import OrderTracking from '../Pages/OrderTracking';
import GlobalStats from '../Pages/GlobalStats';
import InconsistenciesPage from '../Pages/Inconsistencies';
import WebhookErrorsPage from '../Pages/WebhookErrors';
import TicketsPage from '../Pages/Tickets';
import TicketDetailPage from '../Pages/TicketDetail';
import AirtableSyncErrorsPage from '../Pages/AirtableSyncErrors';
import IntegrityPage from '../Pages/Integrity';
import ApiIntakePage from '../Pages/ApiIntake';
import DiagnosticsPage from '../Pages/Diagnostics';
import CollectionPointDetail from '../Pages/CollectionPointDetail';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

// Component to handle external redirects
const ExternalRedirect = ({ to }) => {
  React.useEffect(() => {
    window.location.href = to;
  }, [to]);

  return <div>Redirecting...</div>;
};

const routes = [
  {
    path: '/driver-app',
    element: <ExternalRedirect to={process.env.REACT_APP_DRIVER_APP_URL} />,
  },
  {
    path: '/login',
    element: isDemoMode ? (
      <Navigate to="/admin/route-plans" replace />
    ) : (
      <LayoutAuth>
        <SignIn />
      </LayoutAuth>
    ),
  },
  {
    path: '/admin-login',
    element: <AdminSignIn />,
  },
  {
    path: '/logout',
    element: <LogoutPage />,
  },
  {
    path: '/admin/routes',
    element: (
      <Private allowedRoles={['admin']}>
        <RoutesPage />
      </Private>
    ),
  },
  {
    path: '/admin/routes/:id',
    element: (
      <Private allowedRoles={['admin']}>
        <RouteDetailPage />
      </Private>
    ),
  },
  {
    path: '/admin/hubs',
    element: (
      <Private allowedRoles={['admin']}>
        <HubsPage />
      </Private>
    ),
  },
  {
    path: '/admin/fleet',
    element: (
      <Private allowedRoles={['admin']}>
        <FleetsPage />
      </Private>
    ),
  },
  {
    path: '/admin/orders',
    element: (
      <Private allowedRoles={['admin']}>
        <OrdersPage />
      </Private>
    ),
  },
  {
    path: '/admin/orders/new',
    element: (
      <Private allowedRoles={['admin']}>
        <NewOrderPage />
      </Private>
    ),
  },
  {
    path: '/admin/orders/upload',
    element: (
      <Private allowedRoles={['admin']}>
        <UploadOrders />
      </Private>
    ),
  },
  {
    path: '/admin/orders/:id',
    element: (
      <Private allowedRoles={['admin']}>
        <OrderDetailPage />
      </Private>
    ),
  },
  {
    path: '/admin/drivers',
    element: (
      <Private allowedRoles={['admin']}>
        <DriversPage />
      </Private>
    ),
  },
  {
    path: '/admin/drivers/:id',
    element: (
      <Private allowedRoles={['admin']}>
        <DriverDetailPage />
      </Private>
    ),
  },
  {
    path: '/admin/scheduler',
    element: (
      <Private allowedRoles={['admin']}>
        <SchedulerPage />
      </Private>
    ),
  },
  {
    path: '/admin/route-plans/:type?',
    element: (
      <Private allowedRoles={['admin']}>
        <RoutesChart />
      </Private>
    ),
  },
  {
    path: '/admin/partners',
    element: (
      <Private allowedRoles={['admin']}>
        <PartnersPage />
      </Private>
    ),
  },
  // OVO-133 — global top-nav Sites / Recurring / Collected (partner-scoped
  // via a picker; reuse the partner-detail components).
  {
    path: '/admin/sites',
    element: (
      <Private allowedRoles={['admin']}>
        <AdminCatalogPage kind="sites" />
      </Private>
    ),
  },
  {
    path: '/admin/recurring',
    element: (
      <Private allowedRoles={['admin']}>
        <AdminCatalogPage kind="recurring" />
      </Private>
    ),
  },
  {
    path: '/admin/collected',
    element: (
      <Private allowedRoles={['admin']}>
        <AdminCatalogPage kind="collected" />
      </Private>
    ),
  },
  {
    // OVO-206 — first-class Collection Points ("Caisses") catalog.
    path: '/admin/collection-points',
    element: (
      <Private allowedRoles={['admin']}>
        <AdminCatalogPage kind="collection-points" />
      </Private>
    ),
  },
  {
    path: '/admin/partners/:id',
    element: (
      <Private allowedRoles={['admin']}>
        <PartnerDetailPage />
      </Private>
    ),
  },
  {
    path: '/admin/partners/:id/stats',
    element: (
      <Private allowedRoles={['admin']}>
        <PartnerDetailPage defaultTab="stats" />
      </Private>
    ),
  },
  {
    path: '/admin/partners/:id/routes',
    element: (
      <Private allowedRoles={['admin']}>
        <PartnerDetailPage defaultTab="routes" />
      </Private>
    ),
  },
  {
    path: '/admin/partners/:id/orders',
    element: (
      <Private allowedRoles={['admin']}>
        <PartnerDetailPage defaultTab="orders" />
      </Private>
    ),
  },
  {
    path: '/admin/partners/:id/collected',
    element: (
      <Private allowedRoles={['admin']}>
        <PartnerDetailPage defaultTab="collected" />
      </Private>
    ),
  },
  {
    path: '/admin/partners/:id/tickets',
    element: (
      <Private allowedRoles={['admin']}>
        <PartnerDetailPage defaultTab="tickets" />
      </Private>
    ),
  },
  {
    path: '/admin/partners/:id/viewers',
    element: (
      <Private allowedRoles={['admin']}>
        <PartnerDetailPage defaultTab="viewers" />
      </Private>
    ),
  },
  {
    // OVO-120 — Tours tab (partner-scoped tour mapping). Without this
    // route the tab's `navigate(...)` call fell on no match and dropped
    // the page.
    path: '/admin/partners/:id/tours',
    element: (
      <Private allowedRoles={['admin']}>
        <PartnerDetailPage defaultTab="tours" />
      </Private>
    ),
  },
  {
    // OVO-101 — Sites tab (Address Book).
    path: '/admin/partners/:id/sites',
    element: (
      <Private allowedRoles={['admin']}>
        <PartnerDetailPage defaultTab="sites" />
      </Private>
    ),
  },
  {
    // OVO-206 — Collection points tab (per-partner).
    path: '/admin/partners/:id/collection-points',
    element: (
      <Private allowedRoles={['admin']}>
        <PartnerDetailPage defaultTab="collection-points" />
      </Private>
    ),
  },
  {
    // OVO-113 — Recurring orders tab (schedules).
    path: '/admin/partners/:id/schedules',
    element: (
      <Private allowedRoles={['admin']}>
        <PartnerDetailPage defaultTab="schedules" />
      </Private>
    ),
  },
  {
    path: '/admin/stats',
    element: (
      <Private allowedRoles={['admin']}>
        <GlobalStats />
      </Private>
    ),
  },
  {
    // Monitoring → route inconsistencies (read-only detection list).
    path: '/admin/inconsistencies',
    element: (
      <Private allowedRoles={['admin']}>
        <InconsistenciesPage />
      </Private>
    ),
  },
  {
    // OVO-117 — support tickets: triage list + detail.
    path: '/admin/tickets',
    element: (
      <Private allowedRoles={['admin']}>
        <TicketsPage />
      </Private>
    ),
  },
  {
    path: '/admin/tickets/:id',
    element: (
      <Private allowedRoles={['admin']}>
        <TicketDetailPage />
      </Private>
    ),
  },
  {
    // Monitoring → partner-webhook errors (non-2xx), with per-row delete.
    path: '/admin/webhook-errors',
    element: (
      <Private allowedRoles={['admin']}>
        <WebhookErrorsPage />
      </Private>
    ),
  },
  {
    // OVO-227 — Monitoring → Airtable sync errors (FAILED PATCHes), with resend.
    path: '/admin/airtable-errors',
    element: (
      <Private allowedRoles={['admin']}>
        <AirtableSyncErrorsPage />
      </Private>
    ),
  },
  {
    // #149 — Monitoring → Integrity (order state machine + route-step checks).
    path: '/admin/integrity',
    element: (
      <Private allowedRoles={['admin']}>
        <IntegrityPage />
      </Private>
    ),
  },
  {
    // CT prod readiness (Aug 2026) — dedicated collection-point page (edit +
    // per-box analytics). Declared before any catch-alls.
    path: '/admin/collection-points/:id',
    element: (
      <Private allowedRoles={['admin']}>
        <CollectionPointDetail />
      </Private>
    ),
  },
  {
    // MCP admin assistant Phase 1 — read-only diagnostics (order / day / route).
    path: '/admin/diagnostics',
    element: (
      <Private allowedRoles={['admin']}>
        <DiagnosticsPage />
      </Private>
    ),
  },
  {
    // API intake — raw partner bodies forwarded by ovo-api, with resend/replay.
    path: '/admin/api-intake',
    element: (
      <Private allowedRoles={['admin']}>
        <ApiIntakePage />
      </Private>
    ),
  },
  {
    path: '/admin/users',
    element: (
      <Private allowedRoles={['superadmin']}>
        <AdminUsersPage />
      </Private>
    ),
  },
  {
    path: '/partner',
    element: (
      <Private allowedRoles={['partner_viewer']}>
        <PartnerDetailPage viewOnly />
      </Private>
    ),
  },
  {
    path: '/partner/stats',
    element: (
      <Private allowedRoles={['partner_viewer']}>
        <PartnerDetailPage viewOnly defaultTab="stats" />
      </Private>
    ),
  },
  {
    path: '/partner/routes',
    element: (
      <Private allowedRoles={['partner_viewer']}>
        <PartnerDetailPage viewOnly defaultTab="routes" />
      </Private>
    ),
  },
  {
    // OVO-002 — partner-scoped single-route detail. Backend filters the
    // route to only this partner's stops and renumbers them; mutualised
    // routes don't leak other partners' addresses or polylines.
    path: '/partner/routes/:id',
    element: (
      <Private allowedRoles={['partner_viewer']}>
        <PartnerRouteDetailPage />
      </Private>
    ),
  },
  {
    // OVO-117 — the partner portal's Support page, rendered as a tab of the
    // viewer dashboard so it sits in the main navigation.
    path: '/partner/tickets',
    element: (
      <Private allowedRoles={['partner_viewer']}>
        <PartnerDetailPage viewOnly defaultTab="tickets" />
      </Private>
    ),
  },
  {
    path: '/partner/orders',
    element: (
      <Private allowedRoles={['partner_viewer']}>
        <PartnerDetailPage viewOnly defaultTab="orders" />
      </Private>
    ),
  },
  {
    path: '/partner/collected',
    element: (
      <Private allowedRoles={['partner_viewer']}>
        <PartnerDetailPage viewOnly defaultTab="collected" />
      </Private>
    ),
  },
  {
    // OVO-101 — read-only Address Book (Sites) for the partner-viewer portal.
    path: '/partner/sites',
    element: (
      <Private allowedRoles={['partner_viewer']}>
        <PartnerDetailPage viewOnly defaultTab="sites" />
      </Private>
    ),
  },
  {
    // OVO-206 — read-only Collection points for the partner-viewer portal.
    path: '/partner/collection-points',
    element: (
      <Private allowedRoles={['partner_viewer']}>
        <PartnerDetailPage viewOnly defaultTab="collection-points" />
      </Private>
    ),
  },
  {
    // OVO-113 — read-only Recurring (schedules) for the partner-viewer portal.
    path: '/partner/schedules',
    element: (
      <Private allowedRoles={['partner_viewer']}>
        <PartnerDetailPage viewOnly defaultTab="schedules" />
      </Private>
    ),
  },
  {
    path: '/partner/viewers',
    element: (
      <Private allowedRoles={['partner_viewer']}>
        <PartnerDetailPage viewOnly defaultTab="viewers" />
      </Private>
    ),
  },
  {
    path: '/partner/orders/:id',
    element: (
      <Private allowedRoles={['partner_viewer']}>
        <OrderDetailPage />
      </Private>
    ),
  },
  {
    path: '/t/:orderCode/:token',
    element: <OrderTracking />,
  },
  {
    path: '/*',
    element: <Navigate to="admin/route-plans" replace />,
  },
];

// Keep the assistant mounted across admin navigation; it clears on identity changes.
const router = createBrowserRouter([
  {
    element: (
      <>
        <Outlet />
        <PlannerAssistant />
      </>
    ),
    children: routes,
  },
]);

export default router;
